/**
 * Section 4 of the Check-In tab: the searchable, filterable role table with
 * collapsible groups (by priority, cluster, urgency, …).
 */
import { useMemo, useState } from 'react';
import type { CheckInRole } from '../../../types/taCheckin';
import {
  ClusterChip,
  TierBadge,
  UrgencyChip,
  URG_LABEL,
  URG_ORDER,
  daysBetween,
  fmtDate,
  tierRank,
  urgRank,
} from './checkinUi';
import { TIER_ORDER } from './checkinUi';

type GroupBy =
  | 'priority'
  | 'sheetCluster'
  | 'department'
  | 'urgency'
  | 'reason'
  | 'level'
  | 'hiringManager'
  | 'recruiter'
  | 'none';
type SortBy = 'priority' | 'urgency' | 'department' | 'title' | 'start' | 'level';

const COLUMN_ORDER = [
  'priority',
  'role',
  'urgency',
  'level',
  'department',
  'hiringManager',
  'recruiter',
  'plannedStart',
  'reason',
] as const;
type ColumnKey = (typeof COLUMN_ORDER)[number];

const COLUMN_HEADER: Record<ColumnKey, string> = {
  priority: 'Priority',
  role: 'Role',
  urgency: 'Urgency',
  level: 'Level',
  department: 'Cluster',
  hiringManager: 'Hiring manager',
  recruiter: 'Recruiter',
  plannedStart: 'Start date',
  reason: 'Reason',
};

/** Which column becomes redundant when grouping by that key. */
const SUPPRESSED: Partial<Record<GroupBy, ColumnKey>> = {
  priority: 'priority',
  department: 'department',
  sheetCluster: 'department',
  urgency: 'urgency',
  level: 'level',
  hiringManager: 'hiringManager',
  recruiter: 'recruiter',
  reason: 'reason',
};

const strCompare = (a: string, b: string) => {
  if (!a && b) return 1;
  if (a && !b) return -1;
  return a.localeCompare(b);
};
const dateCompare = (a: string, b: string) => (a || '9999-99-99').localeCompare(b || '9999-99-99');

const SORTERS: Record<SortBy, (a: CheckInRole, b: CheckInRole) => number> = {
  priority: (a, b) => tierRank(a.priority) - tierRank(b.priority),
  urgency: (a, b) => urgRank(a.urgency) - urgRank(b.urgency),
  department: (a, b) => strCompare(a.department, b.department),
  title: (a, b) => strCompare(a.title, b.title),
  start: (a, b) => dateCompare(a.sheetStart || a.plannedStart, b.sheetStart || b.plannedStart),
  level: (a, b) => strCompare(a.level, b.level),
};

function groupKey(r: CheckInRole, g: GroupBy): string {
  switch (g) {
    case 'priority':
      return r.priority;
    case 'department':
      return r.department || 'Unassigned';
    case 'sheetCluster':
      return r.sheetCluster || 'Not in check-in';
    case 'urgency':
      return URG_LABEL[(URG_ORDER as readonly string[]).includes(r.urgency) ? r.urgency : '__none__'];
    case 'reason':
      return r.reason || 'Unspecified';
    case 'level':
      return r.level || 'Unspecified';
    case 'hiringManager':
      return r.hiringManager || 'Unassigned';
    case 'recruiter':
      return r.recruiter || 'Unassigned';
    default:
      return '';
  }
}

function orderGroupKeys(g: GroupBy, keys: string[]): string[] {
  if (g === 'priority') return [...TIER_ORDER].filter((t) => keys.includes(t));
  if (g === 'urgency') return ['Urgent', 'Stretch', 'In time', 'Not set'].filter((k) => keys.includes(k));
  const specials = ['Unassigned', 'Unspecified', 'Not in check-in'];
  return keys
    .filter((k) => !specials.includes(k))
    .sort((a, b) => a.localeCompare(b))
    .concat(keys.filter((k) => specials.includes(k)).sort());
}

function Cell({ col, role: r }: { col: ColumnKey; role: CheckInRole }) {
  switch (col) {
    case 'priority':
      return <TierBadge tier={r.priority} />;
    case 'role':
      return (
        <div>
          <div className="font-medium text-gray-900">{r.title}</div>
          {r.positionId && <div className="text-[11px] text-gray-400">#{r.positionId}</div>}
        </div>
      );
    case 'urgency':
      return <UrgencyChip urgency={r.urgency} />;
    case 'level':
      return r.level ? <span>{r.level}</span> : <span className="text-gray-400">—</span>;
    case 'department':
      return (
        <div>
          <ClusterChip name={r.sheetCluster || r.department} />
          {r.unit && <div className="text-[11px] text-gray-400">{r.unit}</div>}
        </div>
      );
    case 'hiringManager':
      return r.hiringManager ? <span>{r.hiringManager}</span> : <span className="text-gray-400">—</span>;
    case 'recruiter':
      return r.recruiter ? <span>{r.recruiter}</span> : <span className="text-gray-400">—</span>;
    case 'plannedStart': {
      const iso = r.sheetStart || r.plannedStart;
      if (!iso) return <span className="text-gray-400">—</span>;
      const d = daysBetween(iso);
      return (
        <div>
          {fmtDate(iso)}
          {d != null && d > 0 && <div className="text-[11px] text-rose-700">{d} d overdue</div>}
        </div>
      );
    }
    case 'reason':
      return r.reason ? <span>{r.reason}</span> : <span className="text-gray-400">—</span>;
  }
}

export function RoleTable({
  roles,
  activeTier,
  matrixFilterIds,
  onClearExternalFilters,
}: {
  roles: CheckInRole[];
  activeTier: string | null;
  matrixFilterIds: Set<string> | null;
  onClearExternalFilters: () => void;
}) {
  const [search, setSearch] = useState('');
  const [urgFilter, setUrgFilter] = useState('');
  const [clusterFilter, setClusterFilter] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('priority');
  const [sortBy, setSortBy] = useState<SortBy>('priority');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const clusterOptions = useMemo(
    () => Array.from(new Set(roles.map((r) => r.sheetCluster || r.department))).sort(),
    [roles]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roles.filter((r) => {
      if (matrixFilterIds && !matrixFilterIds.has(r.positionId || r.title)) return false;
      if (activeTier !== null && r.priority !== activeTier) return false;
      if (clusterFilter && (r.sheetCluster || r.department) !== clusterFilter) return false;
      if (urgFilter === '__none__' && r.urgency) return false;
      if (urgFilter && urgFilter !== '__none__' && r.urgency !== urgFilter) return false;
      if (q) {
        const hay = [r.title, r.role, r.department, r.sheetCluster, r.hiringManager, r.recruiter, r.unit]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [roles, search, urgFilter, clusterFilter, activeTier, matrixFilterIds]);

  const sorted = useMemo(() => {
    const cmp = SORTERS[sortBy];
    return filtered.slice().sort((a, b) => cmp(a, b) || strCompare(a.title, b.title));
  }, [filtered, sortBy]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: null as string | null, items: sorted }];
    const buckets: Record<string, CheckInRole[]> = {};
    sorted.forEach((r) => {
      const k = groupKey(r, groupBy);
      (buckets[k] = buckets[k] || []).push(r);
    });
    return orderGroupKeys(groupBy, Object.keys(buckets)).map((k) => ({ key: k as string | null, items: buckets[k] }));
  }, [sorted, groupBy]);

  const cols = COLUMN_ORDER.filter((c) => c !== SUPPRESSED[groupBy]);
  const selectCls = 'rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, role, hiring manager, recruiter…"
          className="min-w-56 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <select value={clusterFilter} onChange={(e) => setClusterFilter(e.target.value)} className={selectCls}>
          <option value="">All clusters</option>
          {clusterOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={urgFilter} onChange={(e) => setUrgFilter(e.target.value)} className={selectCls}>
          <option value="">Urgency: all</option>
          <option value="urgent">Urgency: urgent</option>
          <option value="stretch">Urgency: stretch</option>
          <option value="in time">Urgency: in time</option>
          <option value="__none__">Urgency: not set</option>
        </select>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)} className={selectCls}>
          <option value="priority">Group by: Priority</option>
          <option value="sheetCluster">Group by: Cluster</option>
          <option value="department">Group by: Department</option>
          <option value="urgency">Group by: Urgency</option>
          <option value="reason">Group by: Reason for hiring</option>
          <option value="level">Group by: Track & level</option>
          <option value="hiringManager">Group by: Hiring manager</option>
          <option value="recruiter">Group by: Recruiter</option>
          <option value="none">No grouping</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} className={selectCls}>
          <option value="priority">Sort by: Priority</option>
          <option value="urgency">Sort by: Urgency</option>
          <option value="department">Sort by: Cluster</option>
          <option value="title">Sort by: Title (A→Z)</option>
          <option value="start">Sort by: Start date</option>
          <option value="level">Sort by: Track & level</option>
        </select>
        <button
          onClick={() => {
            setSearch('');
            setUrgFilter('');
            setClusterFilter('');
            setGroupBy('priority');
            setSortBy('priority');
            onClearExternalFilters();
          }}
          className="text-sm text-gray-500 underline-offset-2 hover:underline"
        >
          Clear filters
        </button>
        {matrixFilterIds && (
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">
            filtered to {matrixFilterIds.size} role(s) from the matrix
          </span>
        )}
      </div>

      {groups.map(({ key, items }) => {
        const ck = `${groupBy}::${key}`;
        const isCollapsed = key !== null && collapsed.has(ck);
        return (
          <div key={ck} className="mb-2">
            {key !== null && (
              <button
                className="flex w-full items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-left text-sm hover:bg-gray-100"
                onClick={() =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (next.has(ck)) next.delete(ck);
                    else next.add(ck);
                    return next;
                  })
                }
              >
                {groupBy === 'priority' ? (
                  <TierBadge tier={key} />
                ) : (
                  <span className="font-medium text-gray-900">{key}</span>
                )}
                <span className="text-xs text-gray-500">
                  {items.length} role{items.length === 1 ? '' : 's'}
                </span>
                <span className={`ml-auto text-gray-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>
                  ▾
                </span>
              </button>
            )}
            {!isCollapsed && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {cols.map((c) => (
                        <th key={c} className="px-2 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-400">
                          {COLUMN_HEADER[c]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr key={(r.positionId || r.title) + r.title} className="border-t border-gray-100">
                        {cols.map((c) => (
                          <td key={c} className="px-2 py-2 align-top">
                            <Cell col={c} role={r} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      {!sorted.length && (
        <div className="py-8 text-center text-sm text-gray-500">No open roles match these filters.</div>
      )}
    </div>
  );
}
